import type { SetManyUserData } from "@exonn/js-sdk";
import { logger } from "src/logger";
import { useExonnApi } from "src/exonn-api/exonn-api.tsx";
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useDebouncedCallback } from "@exonn/hooks";
import { listenUserUnauthenticated } from "src/state/events.ts";
import type { StorageKey } from "@exonn/storage";

export type UserDataItem<Value = any> = {
  key: StorageKey;
  value: Value | null;
};

export const UserDataApiContext = createContext<null | {
  setUserData: <Value = any>(item: UserDataItem<Value>) => void;
}>(null);

type UserDataState = {
  pendingUserData: {
    name: string;
    payload: string;
    version: string;
  }[];
};

export const UserDataProvider = ({ children }: PropsWithChildren) => {
  const api = useExonnApi();
  const state = useRef<UserDataState>({
    pendingUserData: [],
  });

  const [debouncedManySetUserData, cancelDebouncedManySetUserData] =
    useDebouncedCallback(
      async (params, data: SetManyUserData.RequestBody) => {
        logger.debug("saving user state to database", {
          data,
        });
        return (await api.userData.setManyUserData(params, data)).data;
      },
      300,
      () => {
        state.current.pendingUserData = [];
      },
    );

  useEffect(() => {
    return listenUserUnauthenticated(() => {
      // it makes no sense to send request to save user data after user logged out
      // because this endpoint is protected from unauthorized access
      cancelDebouncedManySetUserData();
    });
  }, [cancelDebouncedManySetUserData]);

  const setUserDataBatched = useCallback(
    (item: UserDataItem) => {
      const existingItemIndex = state.current.pendingUserData.findIndex(
        (innerItem) => innerItem.name === item.key.name,
      );

      if (existingItemIndex !== -1) {
        state.current.pendingUserData[existingItemIndex] = {
          payload: JSON.stringify(item.value),
          name: item.key.name,
          version: item.key.version,
        };
      } else {
        state.current.pendingUserData.push({
          payload: JSON.stringify(item.value),
          name: item.key.name,
          version: item.key.version,
        });
      }

      // todo: log error
      return debouncedManySetUserData(null, {
        items: [...state.current.pendingUserData],
      });
    },
    [debouncedManySetUserData],
  );

  const userDataApi = useMemo(
    () => ({
      setUserData: setUserDataBatched,
    }),
    [setUserDataBatched],
  );

  return (
    <UserDataApiContext.Provider value={userDataApi}>
      {children}
    </UserDataApiContext.Provider>
  );
};
